import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { topic, tracking } from "shared/data";
import { ServiceJournalism } from "shared/data/tracking/module";
import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { thingsToDoQueue$key } from "scmp-app/queries/__generated__/thingsToDoQueue.graphql";

import {
  Container,
  StyledContentItemHomeSecondary,
  ThingsToDoItems,
  Title,
  TitleBasLink,
} from "./styles";
type Props = {
  className?: string;
  reference: thingsToDoQueue$key;
};

export const HomeThingsToDo: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment thingsToDoQueue on Query {
        thingToDoQueue: queue(filter: { name: "homepage_service_journalism_hk" }) {
          settings
          items(first: 3) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...homeSecondaryContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const defaultTitle = "THINGS TO DO: HONG KONG";
  const customText =
    (data?.thingToDoQueue?.settings as undefined | { custom_text?: string })?.custom_text ??
    defaultTitle;
  const thingsToItems = data?.thingToDoQueue?.items?.edges;
  const isDefaultTitle = customText.toUpperCase() === defaultTitle;

  const listOfArticles = (thingsToItems?.map(({ node }) => node.entityId) ?? []).join(",");
  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "things_to_do_hk",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (thingsToItems?.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.ThingsToDo,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        {isDefaultTitle ? (
          <TitleBasLink
            as={topic.whatToDoInHongKong.urlAlias}
            pathname={topic.whatToDoInHongKong.urlAlias}
            query={{ module: ServiceJournalism }}
          >
            <Title>{customText}</Title>
          </TitleBasLink>
        ) : (
          <Title>{customText}</Title>
        )}
        <ThingsToDoItems>
          {thingsToItems?.map(({ node }) => (
            <StyledContentItemHomeSecondary
              key={node.entityId}
              onClick={entityId => {
                sendGA4Tracking({
                  action: "click",
                  category: "recirculation",
                  customized_parameters: {
                    article_id: entityId ?? "",
                    page_type: "homepage",
                    widget_name: "things_to_do_hk",
                  },
                  subcategory: "widget",
                });
              }}
              reference={node}
              withImage
              withSection
              withVideoPlayIcon
            />
          ))}
        </ThingsToDoItems>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeThingsToDo.displayName = "HomeThingsToDo";
